"use client";

import { useEffect, useRef, useState } from "react";
import { useConfirm } from "./ConfirmDialog";

/** 添付の上限。サーバー側の lib/requests.ts と同じ値を持つ */
const FILE_COUNT_MAX = 10;
const FILE_BYTES_MAX = 20 * 1024 * 1024;
const TOTAL_BYTES_MAX = 50 * 1024 * 1024;

const sizeLabel = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MiB` : `${Math.max(1, Math.round(bytes / 1024))} KiB`;

/** サバキラへの依頼文を送る画面。PC 用 */
export default function RequestForm({ world, onBack }: { world: string; onBack: () => void }) {
  const [userName, setUserName] = useState("");
  const [text, setText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [dropping, setDropping] = useState(false);
  const pickerRef = useRef<HTMLInputElement | null>(null);
  const [status, setStatus] = useState("");
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ask, confirmDialog] = useConfirm();

  // ユーザー名はセッションのアカウントから取る
  useEffect(() => {
    let alive = true;
    fetch("/api/auth/session")
      .then((res) => res.json())
      .then((data) => {
        if (alive) setUserName(data.account?.displayName ?? "");
      })
      .catch(() => {
        if (alive) {
          setStatus("ユーザー名を読めない");
          setFailed(true);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  /** 添付を足す。上限を超える分は足さずに文言を出す */
  const addFiles = (added: File[]) => {
    if (added.length === 0) return;
    const next = [...files];
    let total = next.reduce((sum, x) => sum + x.size, 0);
    let refused = "";
    for (const file of added) {
      if (next.length >= FILE_COUNT_MAX) {
        refused = `添付は ${FILE_COUNT_MAX} 個までです`;
        break;
      }
      if (file.size > FILE_BYTES_MAX) {
        refused = `添付は 1 個 ${sizeLabel(FILE_BYTES_MAX)} までです: ${file.name}`;
        continue;
      }
      if (total + file.size > TOTAL_BYTES_MAX) {
        refused = `添付の合計は ${sizeLabel(TOTAL_BYTES_MAX)} までです`;
        continue;
      }
      next.push(file);
      total += file.size;
    }
    setFiles(next);
    setStatus(refused);
    setFailed(refused !== "");
  };

  const send = async () => {
    if (busy || text.trim() === "") return;
    setBusy(true);
    setStatus("送っています");
    setFailed(false);
    try {
      const form = new FormData();
      form.append("world", world);
      form.append("text", text);
      for (const file of files) form.append("files", file, file.name);

      const res = await fetch("/api/admin/requests", { method: "POST", body: form });
      const data = await res.json();
      if (!res.ok) {
        setStatus(data.error ?? "送れない");
        setFailed(true);
        return;
      }
      setText("");
      setFiles([]);
      setStatus(`送りました（${data.folder}・添付 ${data.files} 個）`);
    } catch {
      setStatus("送れない");
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const back = async () => {
    const empty = text.trim() === "" && files.length === 0;
    if (empty || (await ask("書きかけの依頼があります。捨てて戻りますか？", { ok: "捨てて戻る", danger: true }))) onBack();
  };

  const smallButton = (primary: boolean): React.CSSProperties => ({
    padding: "8px 20px",
    fontSize: 14,
    fontFamily: "inherit",
    background: primary ? "var(--accent)" : "var(--bg-field)",
    color: primary ? "#1a1509" : "var(--text)",
    fontWeight: primary ? 700 : 400,
    border: `1px solid ${primary ? "var(--accent)" : "var(--line)"}`,
    borderRadius: 6,
    cursor: "pointer",
  });
  const label: React.CSSProperties = { fontSize: 13, color: "var(--text-dim)", margin: "0 0 6px" };
  const canSend = !busy && text.trim() !== "";

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        justifyContent: "center",
        background: "var(--bg)",
        color: "var(--text)",
        padding: "32px 24px",
      }}
    >
      {confirmDialog}
      <div style={{ width: "100%", maxWidth: 960, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <strong style={{ color: "var(--accent)", letterSpacing: "0.1em" }}>サバキラへの依頼</strong>
          <button style={smallButton(false)} onClick={back}>
            戻る
          </button>
        </div>

        <p style={label}>ユーザー名</p>
        <input
          value={userName}
          readOnly
          style={{
            width: 320,
            fontSize: 15,
            fontFamily: "inherit",
            padding: "9px 12px",
            marginBottom: 18,
            color: "var(--text-dim)",
            background: "var(--bg-field)",
            border: "1px solid var(--line)",
            borderRadius: 6,
          }}
        />

        <p style={label}>依頼文</p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={busy}
          style={{
            flex: 1,
            width: "100%",
            resize: "none",
            outline: "none",
            padding: "12px 14px",
            fontSize: 15,
            lineHeight: 1.7,
            fontFamily: "inherit",
            color: "var(--text)",
            background: "var(--bg-field)",
            border: "1px solid var(--line)",
            borderRadius: 6,
          }}
        />

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDropping(true);
          }}
          onDragLeave={() => setDropping(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDropping(false);
            if (!busy) addFiles(Array.from(e.dataTransfer.files));
          }}
          style={{
            marginTop: 14,
            padding: "10px 12px",
            border: `1px dashed ${dropping ? "var(--accent)" : "var(--line)"}`,
            borderRadius: 6,
            maxHeight: 190,
            overflow: "auto",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button style={smallButton(false)} disabled={busy} onClick={() => pickerRef.current?.click()}>
              ファイルを添付
            </button>
            <span style={{ fontSize: 12, color: "var(--text-dim)" }}>
              ここへ落としても足せます。{FILE_COUNT_MAX} 個まで・1 個 {sizeLabel(FILE_BYTES_MAX)} まで・合計{" "}
              {sizeLabel(TOTAL_BYTES_MAX)} まで
            </span>
            <input
              ref={pickerRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                addFiles(Array.from(e.target.files ?? []));
                e.target.value = "";
              }}
            />
          </div>
          {files.map((file, index) => (
            <div key={`${index}-${file.name}`} style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, fontSize: 13 }}>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {file.name}
              </span>
              <span style={{ color: "var(--text-dim)", flexShrink: 0 }}>{sizeLabel(file.size)}</span>
              <button
                style={{ ...smallButton(false), padding: "3px 10px", fontSize: 12 }}
                disabled={busy}
                onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
              >
                外す
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 14, marginTop: 14 }}>
          <span style={{ fontSize: 13, color: failed ? "var(--danger)" : "var(--text-dim)" }}>{status}</span>
          <button style={{ ...smallButton(true), opacity: canSend ? 1 : 0.5 }} disabled={!canSend} onClick={send}>
            送る
          </button>
        </div>
      </div>
    </div>
  );
}
